"use client"

import { useState } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { TabGroup, TabGroupList, TabGroupTrigger } from "@/components/ui/tab-group"
import { AddSlipDialog } from "./components/add-slip-dialog"
import { SlipTable } from "./components/slip-table"
import { SlipDetailDialog } from "./components/slip-detail-dialog"
import { SearchFilters } from "./components/search-filters"
import { useSlips } from "@/src/hooks/useSlips"
import { useBusinessData } from "@/src/hooks/useBusinessData"
import { SlipTransaction } from "@/src/types/slip"

export default function SlipsManagePage() {
  const [isAddSlipDialogOpen, setIsAddSlipDialogOpen] = useState(false)
  const [detailSlipId, setDetailSlipId] = useState<string | null>(null)
  const [currentFilters, setCurrentFilters] = useState<any>({})
  
  // Fetch slips with filters (includes ALL records: duplicates, failed, manual, API, LINE)
  const { slips, loading, error, pagination, setFilters, refetch } = useSlips({
    per_page: 10,
    ...currentFilters,
  })
  
  // Fetch business data (branches, accounts)
  const { branches, accounts } = useBusinessData()

  /** Generic helper to merge new filters and reset to page 1 */
  const applyFilter = (update: Record<string, any>) => {
    const newFilters = { ...currentFilters, ...update, page: 1, per_page: 10 }
    setCurrentFilters(newFilters)
    setFilters(newFilters)
  }

  const handleSearch       = (searchTerm: string)           => applyFilter({ search: searchTerm })
  const handleAccountFilter = (accountId: string | undefined) => applyFilter({ account_id: accountId })
  const handleBranchFilter  = (branchId:  string | undefined) => applyFilter({ branch_id: branchId })
  const handleStatusFilter  = (status:    string)           => applyFilter({ verification_status: status || undefined })
  const handleChannelFilter = (channel:   string)           => applyFilter({ channel: channel || undefined })

  const handlePageChange = (page: number) => {
    const newFilters = { ...currentFilters, page, per_page: 10 }
    setCurrentFilters(newFilters)
    setFilters(newFilters)
  }
  
  const handleSlipAdded = () => refetch()

  const handleViewDetail = (slip: SlipTransaction) => setDetailSlipId(slip.id)

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">จัดการสลิป</h1>
          <Button 
            className="bg-brand-light-red hover:bg-brand-light-red/90 text-white"
            onClick={() => setIsAddSlipDialogOpen(true)}
          >
            เพิ่มสลิปใหม่
          </Button>
        </div>

        {/* Subtitle */}
        <h2 className="text-lg font-normal text-gray-900">ประวัติการตรวจสอบสลิป</h2>

        {/* Branch Tabs */}
        <TabGroup 
          defaultValue="all" 
          color="brand-blue"
          onValueChange={(value) => handleBranchFilter(value === "all" ? undefined : value)}
        >
          <TabGroupList>
            <TabGroupTrigger value="all">สาขาทั้งหมด</TabGroupTrigger>
            {branches.map((branch) => (
              <TabGroupTrigger key={branch.id} value={branch.id}>
                {branch.branch_name}
              </TabGroupTrigger>
            ))}
          </TabGroupList>
        </TabGroup>
        
        {/* Search and Filters */}
        <SearchFilters
          accounts={accounts}
          onSearch={handleSearch}
          onAccountFilter={handleAccountFilter}
          onStatusFilter={handleStatusFilter}
          onChannelFilter={handleChannelFilter}
        />

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            <p className="font-medium">เกิดข้อผิดพลาด</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Table */}
        <SlipTable
          slips={slips}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onViewDetail={handleViewDetail}
        />
      </div>

      {/* Slip Detail Dialog */}
      <SlipDetailDialog
        transactionId={detailSlipId}
        open={detailSlipId !== null}
        onOpenChange={(open) => { if (!open) setDetailSlipId(null) }}
      />

      {/* Add Slip Dialog */}
      <AddSlipDialog
        open={isAddSlipDialogOpen}
        onOpenChange={setIsAddSlipDialogOpen}
        branches={branches}
        accounts={accounts}
        onSuccess={handleSlipAdded}
      />
    </DashboardLayout>
  )
}
